import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Hofladen-Automat kaufen“ (/hofladen-automat).
 *
 * Ziel-Keyword: „hofladen automat“ (~400 Suchen/Monat, geringer Wettbewerb) mit
 * Kaufabsicht. Struktur/SEO analog zur Eierautomat-Seite: Title, Meta, Canonical,
 * JSON-LD (Product + FAQPage), Fokus auf Shop-CTAs, Ratgeber-Link bewusst
 * sekundär. Verlinkt den bestehenden Blogartikel „Einen Hofladen-Automaten
 * aufstellen“ (informationaler Zubringer → keine Kannibalisierung).
 *
 * Produkt-Constraint: Der Automat hat (Stand 2026-08) KEINE Kühlung – daher nur
 * ungekühlte/lagerfähige Hofprodukte bewerben (Eier, Kartoffeln, Backwaren,
 * Honig, Marmelade, Wein, Nudeln), keine Milch/Fleisch/Käse.
 */
@Component({
  selector: 'app-hofladen-automat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './hofladen-automat-page.component.html',
  styleUrls: ['./hofladen-automat-page.component.scss', '../cluster-landing.shared.scss'],
})
export class HofladenAutomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  /** Slug des passenden Ratgeber-Artikels (informationaler Zubringer). */
  readonly blogHofladen = 'einen-hofladen-automaten-aufstellen';
  /** Öffentlicher Live-Link einer echten Box (Echtzeit-Verfügbarkeit) als iframe-Beispiel. */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/obsthof-zink/U-MB-00022'
  );

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@hla.faq1.q:Was kostet ein Hofladen-Automat?`,
      a: $localize`:@@hla.faq1.a:Die marktbox als Hofladen-Automat gibt es ab 6.800 € zum Kauf oder ab 300 € pro Monat zur Miete – je nach Modell (24 oder 39 Fächer) und Zubehör.`,
    },
    {
      q: $localize`:@@hla.faq2.q:Welche Produkte eignen sich für einen Hofladen-Automaten?`,
      a: $localize`:@@hla.faq2.a:Ideal sind ungekühlte, lagerfähige Hofprodukte wie Eier, Kartoffeln, Backwaren, Honig, Marmelade, Nudeln oder Wein. Eine Kühlung ist aktuell nicht verfügbar – gekühlte Waren wie Milch oder Fleisch eignen sich daher noch nicht.`,
    },
    {
      q: $localize`:@@hla.faq3.q:Hofladen-Automat kaufen oder mieten?`,
      a: $localize`:@@hla.faq3.a:Beides ist möglich. Kaufen lohnt sich bei dauerhaftem Betrieb, Mieten senkt die Einstiegshürde. Eine ausführliche Entscheidungshilfe findest du in unserem Ratgeber.`,
    },
    {
      q: $localize`:@@hla.faq4.q:Wo kann ich einen Hofladen-Automaten aufstellen?`,
      a: $localize`:@@hla.faq4.a:Die marktbox lässt sich flexibel aufstellen – direkt ab Hof, am Feldrand, vor dem Laden oder an gut sichtbaren Standorten mit Laufkundschaft. Wichtig sind Sichtbarkeit und einfache Erreichbarkeit.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@hla.metaTitle:Hofladen-Automat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@hla.metaDesc:Hofladen-Automat kaufen oder mieten: regionale Hofprodukte 24/7 verkaufen – bargeldlos, ohne Personal. marktbox ab 6.800 € oder 300 €/Monat mieten.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@hla.metaKeywords:Hofladen Automat, Hofladen-Automat kaufen, Hofautomat, Automat Direktvermarktung, Selbstbedienung Hofladen, regionale Produkte Automat, marktbox`,
    });

    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('hofladen-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Hofladen-Automat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('hofladen-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/hofladen-automat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
